import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Trophy, 
  Users,
  ChevronLeft,
  Loader2,
  Award,
  Calendar,
  TableIcon
} from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

export default function LeagueDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [league, setLeague] = useState(null);
  const [myTeams, setMyTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const fetchData = async () => {
    try {
      const [leagueRes, teamsRes] = await Promise.all([
        api.get(`/leagues/${id}`),
        api.get('/teams/my'),
      ]);
      setLeague(leagueRes.data);
      setMyTeams(teamsRes.data?.filter(t => t.captain_id === user?.id) || []);
    } catch (error) {
      toast.error('Liga nicht gefunden');
      navigate('/leagues');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const isRegistered = league?.registered_teams?.some(
    t => myTeams.some(mt => mt.id === t.team_id)
  );

  const handleRegister = async () => {
    if (!selectedTeam) {
      toast.error('Bitte wähle ein Team');
      return;
    }

    setRegistering(true);
    try {
      await api.post(`/leagues/${id}/register?team_id=${selectedTeam}`);
      toast.success('Team erfolgreich angemeldet!');
      await fetchData();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-48 rounded-2xl mb-4" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (!league) return null;

  const leader = league.standings?.[0];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="league-details">
        {/* Header */}
        <div className="flex items-start gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={
                league.status === 'registration' ? 'bg-tunis-teal text-white' :
                league.status === 'in_progress' ? 'bg-amber-500 text-white' :
                'bg-slate-500 text-white'
              }>
                {league.status === 'registration' ? 'Anmeldung offen' :
                 league.status === 'in_progress' ? 'Läuft' : 'Beendet'}
              </Badge>
              <Badge variant="outline">{league.season}</Badge>
            </div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-tunis-blue">
              {league.name}
            </h1>
            <p className="text-muted-foreground mt-1">{league.description}</p>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-tunis-blue mx-auto mb-2" />
              <div className="text-sm text-muted-foreground">Teams</div>
              <div className="font-medium">{league.team_count}/{league.max_teams}</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 text-tunis-blue mx-auto mb-2" />
              <div className="text-sm text-muted-foreground">Spieltage</div>
              <div className="font-medium">{league.fixtures?.length || 0}</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <div className="text-sm text-muted-foreground">Eintritt</div>
              <div className="font-bold text-tunis-blue">{formatCurrency(league.entry_fee)}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white border-0">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 mx-auto mb-2" />
              <div className="text-sm text-white/80">Preisgeld</div>
              <div className="font-bold text-lg">{formatCurrency(league.prize_pool)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Registration */}
        {league.status === 'registration' && !isRegistered && myTeams.length > 0 && (
          <Card className="border-purple-500 border-2 mb-8">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg text-tunis-blue mb-4">Team anmelden</h3>
              <div className="flex gap-4">
                <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Team auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {myTeams.map(team => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name} ({team.member_count} Mitglieder)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  onClick={handleRegister}
                  disabled={registering || !selectedTeam}
                >
                  {registering ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <>Anmelden ({formatCurrency(league.entry_fee)})</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isRegistered && (
          <Card className="bg-purple-500/10 border-purple-500 mb-8">
            <CardContent className="p-4 flex items-center gap-3">
              <Award className="w-6 h-6 text-purple-500" />
              <span className="font-medium text-purple-700">Dein Team ist angemeldet!</span>
            </CardContent>
          </Card>
        )}

        {/* Tabs: Tabelle & Spielplan */}
        <Tabs defaultValue="standings">
          <TabsList className="mb-6">
            <TabsTrigger value="standings" data-testid="tab-standings">
              <TableIcon className="w-4 h-4 mr-2" />
              Tabelle
            </TabsTrigger>
            <TabsTrigger value="fixtures" data-testid="tab-fixtures">
              <Calendar className="w-4 h-4 mr-2" />
              Spielplan
            </TabsTrigger>
          </TabsList>

          {/* Standings Table */}
          <TabsContent value="standings">
            <Card className="border-slate-200" data-testid="standings-card">
              <CardContent className="p-0">
                {league.standings?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead className="text-center">Sp</TableHead>
                        <TableHead className="text-center">S</TableHead>
                        <TableHead className="text-center">U</TableHead>
                        <TableHead className="text-center">N</TableHead>
                        <TableHead className="text-center">TD</TableHead>
                        <TableHead className="text-center font-bold">Pkt</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {league.standings.map((team, idx) => (
                        <TableRow 
                          key={team.team_id}
                          className={idx === 0 ? 'bg-amber-50' : idx < 3 ? 'bg-green-50/50' : ''}
                        >
                          <TableCell className="font-bold">
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                          </TableCell>
                          <TableCell className="font-medium">{team.team_name}</TableCell>
                          <TableCell className="text-center">{team.played}</TableCell>
                          <TableCell className="text-center text-green-600">{team.won}</TableCell>
                          <TableCell className="text-center text-amber-600">{team.drawn}</TableCell>
                          <TableCell className="text-center text-red-600">{team.lost}</TableCell>
                          <TableCell className="text-center">
                            {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                          </TableCell>
                          <TableCell className="text-center font-bold text-tunis-blue">
                            {team.points}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <TableIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-muted-foreground">Liga noch nicht gestartet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fixtures */}
          <TabsContent value="fixtures">
            <Card className="border-slate-200" data-testid="fixtures-card">
              <CardContent className="p-4">
                {league.fixtures?.length > 0 ? (
                  <div className="space-y-3">
                    {league.fixtures.map((fixture) => (
                      <div
                        key={fixture.id}
                        className={`p-4 rounded-xl border ${
                          fixture.status === 'completed' 
                            ? 'border-slate-200 bg-slate-50' 
                            : 'border-purple-200 bg-purple-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline">Spieltag {fixture.match_day}</Badge>
                          <Badge className={fixture.status === 'completed' ? 'bg-slate-500' : 'bg-purple-500'}>
                            {fixture.status === 'completed' ? 'Beendet' : 'Geplant'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-4 items-center">
                          <div className="text-right">
                            <div className="font-medium">{fixture.team1_name}</div>
                          </div>
                          <div className="text-center">
                            {fixture.status === 'completed' ? (
                              <span className="font-bold text-2xl">
                                {fixture.score1} : {fixture.score2}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">vs</span>
                            )}
                          </div>
                          <div className="text-left">
                            <div className="font-medium">{fixture.team2_name}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-muted-foreground">Spielplan wird erstellt wenn die Liga startet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
